import { createSlice, isAnyOf } from "@reduxjs/toolkit"
import { get } from "nodash"
import { AuthenticationError } from "./constants"
import {
    answerLoginChallenge,
    getPasswordPolicy,
    login,
    refreshCredentials,
    resetPassword,
    sendPasswordResetCode,
    verifyAppleLogin,
    verifyMicrosoftLogin,
} from "./thunks"
import { AuthenticationChallengeRequest, AuthenticationSliceState } from "./types"

const initialState = {
    isAuthenticated: false,
    isLoading: false,
    isVerifyingSso: false,
    isRefreshingCredentials: false,
    needsCredentialRefresh: true,
    challenge: undefined,
    expires: undefined,
    passwordPolicy: undefined,
    errorMessage: undefined,
} as AuthenticationSliceState

export const slice = createSlice({
    name: "authentication",
    initialState,
    reducers: {
        resetAuthenticationError: (state) => {
            state.authenticationErrorMessage = undefined
        },
        setIsVerifyingSso: (state, action: { payload: boolean }) => {
            state.isVerifyingSso = action.payload
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(login.rejected, (state, action) => {
                if (typeof action.payload === "string") {
                    state.authenticationErrorMessage = action.payload
                    return
                }

                // @todo -- the typing feels a bit awkward here, see if we can improve that in the source of thunk somehow
                const challenge = get(action, "payload.authenticationChallenge") as
                    | undefined
                    | AuthenticationChallengeRequest

                if (challenge) {
                    const {
                        requireNewPassword,
                        requireEmailConfirmationCode,
                        requireDeviceConfirmation,
                        deviceKey,
                    } = challenge

                    state.challenge = {
                        requireNewPassword,
                        requireEmailConfirmationCode,
                        requireDeviceConfirmation,
                        deviceKey,
                    }
                }
            })
            .addCase(refreshCredentials.pending, (state) => {
                state.needsCredentialRefresh = false
                state.isRefreshingCredentials = true
            })
            .addCase(refreshCredentials.rejected, (state) => {
                state.isAuthenticated = false
                state.isRefreshingCredentials = false
            })
            .addCase(refreshCredentials.fulfilled, (state) => {
                state.isRefreshingCredentials = false
                state.isAuthenticated = true
            })
            .addCase(getPasswordPolicy.fulfilled, (state, action) => {
                state.passwordPolicy = action.payload
            })
            .addCase(sendPasswordResetCode.rejected, (state) => {
                state.authenticationErrorMessage = AuthenticationError.InvalidConfirmationCode
                state.isLoading = false
            })
            .addCase(resetPassword.rejected, (state) => {
                state.authenticationErrorMessage = AuthenticationError.InvalidPasswordReset
                state.isLoading = false
            })
            .addCase(verifyMicrosoftLogin.rejected, (state, action) => {
                if (typeof action.payload !== "string") return
                state.authenticationErrorMessage = action.payload
            })
            .addCase(verifyAppleLogin.rejected, (state, action) => {
                if (typeof action.payload !== "string") return
                state.authenticationErrorMessage = action.payload
            })
            .addMatcher(isAnyOf(sendPasswordResetCode.pending, resetPassword.pending), (state) => {
                state.authenticationErrorMessage = undefined
                state.isLoading = true
            })
            .addMatcher(
                isAnyOf(sendPasswordResetCode.fulfilled, resetPassword.fulfilled),
                (state) => {
                    state.authenticationErrorMessage = undefined
                    state.isLoading = false
                }
            )
            .addMatcher(
                isAnyOf(
                    answerLoginChallenge.fulfilled,
                    login.fulfilled,
                    verifyAppleLogin.fulfilled,
                    verifyMicrosoftLogin.fulfilled
                ),
                (state, action) => {
                    state.expires = get(action, "payload.credentials.expires") as number | undefined
                    state.challenge = undefined
                    state.isLoading = false
                    state.isAuthenticated = true
                }
            )
            .addMatcher(
                isAnyOf(
                    answerLoginChallenge.pending,
                    login.pending,
                    verifyAppleLogin.pending,
                    verifyMicrosoftLogin.pending
                ),
                (state) => {
                    state.authenticationErrorMessage = undefined
                    state.isLoading = true
                    state.challenge = undefined
                }
            )
            .addMatcher(
                isAnyOf(
                    answerLoginChallenge.fulfilled,
                    answerLoginChallenge.rejected,
                    login.rejected,
                    login.fulfilled,
                    verifyAppleLogin.rejected,
                    verifyAppleLogin.fulfilled,
                    verifyMicrosoftLogin.rejected,
                    verifyMicrosoftLogin.fulfilled
                ),
                (state) => {
                    state.isLoading = false
                    state.isVerifyingSso = false
                }
            )
    },
})

export const { actions, reducer } = slice
export const { resetAuthenticationError: resetAuthenticationErrorAction, setIsVerifyingSso } =
    actions

export default reducer
